<template>
  <div class="carousel">
    <div class="carousel-wrapper">
      <Swiper
        :key="isMobile ? 'mobile' : 'pc'"
        ref="swiperRef"
        :looped-slides="props.items.length * 2"
        :modules="[Autoplay, FreeMode]"
        :allow-touch-move="true"
        :autoplay="autoplayConfig"
        :breakpoints="swiperBreakpoints"
        class="carousel-swiper"
        :class="{ 'is-marquee-mode': !isMobile }"
        @swiper="onSwiper"
        :grab-cursor="true"
        @slideChange="onSlideChange"
      >
        <SwiperSlide v-for="(card, index) in items" :key="`card-${index}`" class="carousel-slide">
          <PowerfulCard :item="card" />
        </SwiperSlide>
      </Swiper>
    </div>
    <div class="controls">
      <SwitchBtn class="pc-only" type="prev" variant="grey-large" @click="prev" />
      <div class="dots">
        <Dot
          v-for="(item, index) in items"
          :key="item.title"
          :active="currentIndex === index"
          @click="goTo(index)"
        />
      </div>
      <SwitchBtn class="pc-only" type="next" variant="grey-large" @click="next" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useBreakpoint } from '@rendora/design/index';
import { useThrottleFn } from '@vueuse/core';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Autoplay, FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SwitchBtn from '~/design/components/buttons/switch-btn.vue';
import Dot from './dot.vue';
import PowerfulCard from './powerful-card.vue';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

const props = withDefaults(
  defineProps<{
    items: Array<{
      img?: string;
      title?: string;
      desc?: string;
      bgcolor?: string;
      link?: string;
      comingSoon?: boolean;
      linkDesc?: string;
      imgAlt?: string;
    }>;
    /** 连续滚动速度，数值越小越快（单位：毫秒/张）默认 3000 */
    scrollSpeed?: number;
    /** 按钮点击切换动画时长（毫秒）默认 800 */
    transitionSpeed?: number;
  }>(),
  {
    scrollSpeed: 3000,
    transitionSpeed: 800,
  },
);

const step = 1; // 每次按钮切换 1 张
const btnTransitionSpeed = computed(() => (isMobile.value ? 300 : 600)); // 按钮切换动画时长（毫秒）

// 连续滚动的 speed（一张卡片滚过需要的时间）
const scrollSpeed = computed(() => props.scrollSpeed);

// 自动播放配置 - delay: 0 实现连续滚动
const autoplayConfig = computed(() => ({
  delay: 0,
  disableOnInteraction: false,
  pauseOnMouseEnter: true,
}));

// 使用 breakpoints 配置响应式
const swiperBreakpoints = computed(() => ({
  // 移动端 (默认)
  0: {
    // slidesPerView: 1.2 as const,
    slidesPerView: 'auto' as const,
    spaceBetween: 16,
    loop: true,
    autoplay: false,
    freeMode: true,
    speed: 300,
    centeredSlides: true,
  },
  // PC端 (769px 及以上)
  769: {
    slidesPerView: 'auto' as const,
    spaceBetween: 40,
    loop: true,
    autoplay: autoplayConfig.value,
    speed: scrollSpeed.value,
  },
}));

let swiperInstance: SwiperType | null = null;
const currentIndex = ref(0);

function onSwiper(swiper: SwiperType) {
  swiperInstance = swiper;
  if (!isMobile.value) {
    swiperInstance.autoplay.start();
  }

  // 监听每次过渡结束
  swiper.on('transitionEnd', () => {
    // 如果是移动端，不处理跑马灯逻辑
    if (isMobile.value) return;

    // PC端跑马灯逻辑：确保动画结束后立即继续下一帧
    setTimeout(() => {
      if (swiperInstance && !isMobile.value) {
        // 恢复速度和 easing（虽有CSS覆盖，但保持状态一致）
        swiperInstance.params.speed = scrollSpeed.value;
        if (swiperInstance.wrapperEl) {
          swiperInstance.wrapperEl.style.transitionTimingFunction = 'linear';
        }
        // 关键点：如果自动播放意外停止了（例如切换视图后），强制重启
        if (!swiperInstance.autoplay.running) {
          swiperInstance.autoplay.start();
        }
      }
    }, 0);
  });
}

// 使用节流来减少 slideChange 触发频率，避免指示器频繁更新造成抖动
const updateCurrentIndex = useThrottleFn(() => {
  if (swiperInstance) {
    currentIndex.value = swiperInstance.realIndex;
  }
}, 100);

function onSlideChange() {
  updateCurrentIndex();
}

function next() {
  if (!swiperInstance) return;

  swiperInstance.autoplay.stop();
  // @ts-ignore
  swiperInstance.loopFix();

  // Set easing for manual slide
  if (swiperInstance.wrapperEl) {
    swiperInstance.wrapperEl.style.transitionTimingFunction = 'ease-out';
  }

  // Use activeIndex for calculation to ensure consistent forward movement
  const current = Math.round(swiperInstance.activeIndex);
  const targetIndex = current + step;

  swiperInstance.slideTo(targetIndex, btnTransitionSpeed.value);

  swiperInstance.once('transitionEnd', () => {
    if (swiperInstance) {
      // Loop fix after transition to ensure we don't run out of slides
      // @ts-ignore
      swiperInstance.loopFix();

      if (!isMobile.value) {
        if (swiperInstance.wrapperEl) {
          swiperInstance.wrapperEl.style.transitionTimingFunction = ''; // Restore class-based linear easing
        }
        setTimeout(() => {
          if (swiperInstance) {
            swiperInstance.params.speed = scrollSpeed.value;
            swiperInstance.autoplay.start();
          }
        }, 0);
      }
    }
  });
}

function prev() {
  if (!swiperInstance) return;

  swiperInstance.autoplay.stop();
  // @ts-ignore
  swiperInstance.loopFix();

  // Set easing for manual slide
  if (swiperInstance.wrapperEl) {
    swiperInstance.wrapperEl.style.transitionTimingFunction = 'ease-out';
  }

  // Use activeIndex for calculation to ensure consistent backward movement
  const current = Math.round(swiperInstance.activeIndex);
  const targetIndex = current - step;

  swiperInstance.slideTo(targetIndex, btnTransitionSpeed.value);

  swiperInstance.once('transitionEnd', () => {
    if (swiperInstance) {
      // Loop fix after transition
      // @ts-ignore
      swiperInstance.loopFix();

      if (!isMobile.value) {
        if (swiperInstance.wrapperEl) {
          swiperInstance.wrapperEl.style.transitionTimingFunction = ''; // Restore class-based linear easing
        }
        setTimeout(() => {
          if (swiperInstance) {
            swiperInstance.params.speed = scrollSpeed.value;
            swiperInstance.autoplay.start();
          }
        }, 0);
      }
    }
  });
}
function goTo(index: number) {
  if (index === currentIndex.value || !swiperInstance || isMobile.value) return;

  // 暂停自动播放
  if (!isMobile.value) {
    swiperInstance.autoplay.stop();
    if (swiperInstance.wrapperEl) {
      swiperInstance.wrapperEl.style.transitionTimingFunction = 'ease-out';
    }
  }

  // 跳转到指定索引
  swiperInstance.slideToLoop(index, btnTransitionSpeed.value);

  // 恢复原速和自动播放
  if (!isMobile.value) {
    setTimeout(() => {
      if (swiperInstance) {
        if (swiperInstance.wrapperEl) {
          swiperInstance.wrapperEl.style.transitionTimingFunction = ''; // Restore class-based linear easing
        }
        swiperInstance.params.speed = scrollSpeed.value;
        swiperInstance.autoplay.start();
      }
    }, btnTransitionSpeed.value + 50);
  }
}

onUnmounted(() => {
  if (swiperInstance) {
    swiperInstance.destroy();
    swiperInstance = null;
  }
});
</script>

<style lang="scss" scoped src="./styles/marquee-swiper-mobile.scss" />
<style lang="scss" scoped src="./styles/marquee-swiper-pc.scss" />
